import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { auth, db } from "../../firebase";
import { generateKeyPair, exportKey, derivePublicKey, encryptPrivateKey, decryptPrivateKey } from "../../utils/crypto";
import { collection, query, where, getDocs, doc, updateDoc, getDoc, setDoc, deleteField, arrayUnion } from "firebase/firestore";

export default function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  // Forgot Password State
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetStatus, setResetStatus] = useState({ type: '', msg: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      let emailToUse = identifier;
      const isEmail = identifier.includes("@");

      if (!isEmail) {
        // 1. Try Secure Username Lookup via 'usernames' collection
        const usernameRef = doc(db, "usernames", identifier.toLowerCase());
        const usernameSnap = await getDoc(usernameRef);

        if (usernameSnap.exists()) {
          emailToUse = usernameSnap.data().email;
        } else {
          // 2. Fallback: Try Legacy Lookup (for existing users who haven't been migrated)
          // This might fail if security rules are strict, but it restores previous behavior for now.
          try {
            const q = query(collection(db, "users"), where("username", "==", identifier.toLowerCase()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              const userDoc = querySnapshot.docs[0];
              emailToUse = userDoc.data().email;
            } else {
              setError("Username not found.");
              return;
            }
          } catch (legacyErr) {
            console.warn("Legacy lookup failed:", legacyErr);
            // If both fail, we can't login with username
            setError("Username not found or access denied.");
            return;
          }
        }
      }

      // Set a flag to prevent race-condition logout in App.js
      sessionStorage.setItem("isLoggingIn", "true");
      const res = await signInWithEmailAndPassword(auth, emailToUse, password);

      // Key Restoration Logic
      // Key Restoration Logic
      const uid = res.user.uid;
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);

      // Secure Key Reference
      const keysRef = doc(db, "users", uid, "private_data", "keys");
      const keysSnap = await getDoc(keysRef);

      let privateKeyJwk = localStorage.getItem(`privKey_${uid}`);
      let publicKeyJwk = null;
      let keyUpdated = false;

      // 1. Try to load from Secure Storage (New Way)
      if (keysSnap.exists() && keysSnap.data().encryptedPrivateKey) {
        try {
          // Decrypt using the password entered during login
          const decrypted = await decryptPrivateKey(keysSnap.data().encryptedPrivateKey, password);
          if (decrypted) {
            privateKeyJwk = decrypted;
            localStorage.setItem(`privKey_${uid}`, privateKeyJwk);
          }
        } catch (e) {
          console.error("Failed to decrypt private key:", e);
          // ⚠️ Decryption failed (password mismatch or key corruption).
          // We log it only. User requested removal of intrusive alerts.
          console.warn("Warning: Could not decrypt secure history key.");
        }
      }
      // 2. Fallback: Check Legacy Insecure Storage (Migration)
      else if (userSnap.exists() && userSnap.data().encryptedPrivateKey) {
        const legacyKey = userSnap.data().encryptedPrivateKey;
        // It's a plain JWK string in the old system
        privateKeyJwk = legacyKey;
        localStorage.setItem(`privKey_${uid}`, privateKeyJwk);

        // MIGRATE NOW: Encrypt and move to secure storage
        try {
          const encryptedData = await encryptPrivateKey(legacyKey, password);
          await setDoc(keysRef, { encryptedPrivateKey: encryptedData });

          // Remove from insecure public doc
          await updateDoc(userRef, {
            encryptedPrivateKey: deleteField()
          });
          // Migration successful

        } catch (migErr) {
          console.error("Migration failed:", migErr);
        }
      }

      // 3. Public Key Retrieval
      if (userSnap.exists() && userSnap.data().publicKey) {
        publicKeyJwk = userSnap.data().publicKey;
      }

      if (!privateKeyJwk) {
        const keyPair = await generateKeyPair();
        publicKeyJwk = await exportKey(keyPair.publicKey);
        privateKeyJwk = await exportKey(keyPair.privateKey);
        localStorage.setItem(`privKey_${uid}`, privateKeyJwk);
        keyUpdated = true;
      } else if (!publicKeyJwk) {
        publicKeyJwk = await derivePublicKey(privateKeyJwk);
        keyUpdated = true;
      }

      let deviceId = localStorage.getItem("deviceId");
      if (!deviceId) {
        deviceId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
        localStorage.setItem("deviceId", deviceId);
      }

      // MULTI-DEVICE SUPPORT: Append device to allow Android & Web simultaneously
      const thisDevice = {
        deviceId,
        publicKey: publicKeyJwk,
        lastActive: Date.now()
      };

      const updateData = {
        devices: arrayUnion(thisDevice),
        lastActive: Date.now(),
        emailVerified: res.user.emailVerified // Sync Verification Status
      };

      if (keyUpdated || (userSnap.exists() && !userSnap.data().encryptedPrivateKey)) {
        updateData.publicKey = publicKeyJwk;
        updateData.encryptedPrivateKey = privateKeyJwk;
      }

      if (userSnap.exists()) {
        await updateDoc(userRef, updateData);
      } else {
        await setDoc(userRef, {
          ...updateData,
          email: res.user.email || emailToUse,
          uid: uid
        });
      }

      // Auto-Repair Username Mapping (Self-Heal) - ENSURES USERNAME LOGIN WORKS
      if (userSnap.exists() && userSnap.data().username) {
        const lowerUsername = userSnap.data().username.toLowerCase();
        const usernameRefRepair = doc(db, "usernames", lowerUsername);
        try {
          await setDoc(usernameRefRepair, {
            email: res.user.email || emailToUse,
            uid: uid
          }, { merge: true });
        } catch (e) { console.warn("Auto-repair username failed", e); }
      }

    } catch (err) {
      console.error("Login encountered an error:", err);

      let errorMsg = "An unexpected error occurred. Please try again.";

      // Handle standard Firebase Auth errors
      switch (err.code) {
        case "auth/user-not-found":
          errorMsg = "No account found with these credentials.";
          break;
        case "auth/wrong-password":
          errorMsg = "Incorrect password. Please try again or reset it.";
          break;
        case "auth/invalid-email":
          errorMsg = "The email address format is invalid.";
          break;
        case "auth/too-many-requests":
          errorMsg = "Access temporarily blocked due to too many failed attempts. Please try again later.";
          break;
        case "auth/user-disabled":
          errorMsg = "This account has been disabled. Please contact support.";
          break;
        case "auth/network-request-failed":
          errorMsg = "Network error. Please check your internet connection.";
          break;
        case "permission-denied":
          errorMsg = "Access denied. If using Username, please try Email instead.";
          break;
        default:
          // Show tech details for debugging purposes as requested
          errorMsg = `Error: ${err.message}`;
      }

      setError(errorMsg);
    }
  };


  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail) return;

    setResetStatus({ type: 'loading', msg: 'Sending reset link...' });

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetStatus({ type: 'success', msg: 'Password reset email sent! Check your inbox.' });
    } catch (error) {
      console.error("Reset Password Error:", error);
      let errorMsg = "Failed to send reset email.";
      if (error.code === 'auth/user-not-found') errorMsg = "No account found with this email.";
      if (error.code === 'auth/invalid-email') errorMsg = "Invalid email address.";
      setResetStatus({ type: 'error', msg: errorMsg });
    }
  };

  return (
    <div className="p-8 w-full max-w-md mx-auto">
      <Link to="/" className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600 transition-colors mb-6 group">
        <svg className="w-4 h-4 mr-2 transform group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Back to Home
      </Link>

      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Welcome Back</h2>
        <p className="text-gray-500">Sign in to continue chatting</p>
      </div>

      {/* Link Device access */}
      <div className="mb-6 flex justify-center">
        <Link to="/link-device-start" className="flex items-center space-x-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold hover:bg-indigo-100 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 17h.01M8 11h16M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>Link with QR Code</span>
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg animate-fade-in shadow-sm relative">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Login Failed</h3>
              <p className="text-sm text-red-700 mt-1 leading-snug">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleLogin} className="space-y-6 text-left">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1 ml-1">Email or Username</label>
          <input
            type="text"
            required
            className="w-full bg-white px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-gray-800 placeholder-gray-400 shadow-sm"
            placeholder="Enter your email or username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
          />
        </div>

        <div>
          <div className="flex justify-between items-center mb-1 ml-1">
            <label className="block text-sm font-semibold text-gray-700">Password</label>
            <button
              type="button"
              onClick={() => setShowResetModal(true)}
              className="text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline tabindex='-1'"
            >
              Forgot Password?
            </button>
          </div>
          <input
            type="password"
            required
            className="w-full bg-white px-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all text-gray-800 placeholder-gray-400 shadow-sm"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-lg hover:bg-blue-700 focus:ring-4 focus:ring-blue-300 transition-all shadow-md transform active:scale-[0.98] mt-2"
        >
          SIGN IN
        </button>
      </form>

      <div className="mt-8 text-xs text-gray-500 flex justify-center space-x-4 border-t pt-6 border-gray-100 pb-2">
        <Link to="/privacy-policy" className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
        <span className="text-gray-300">|</span>
        <Link to="/terms-of-service" className="hover:text-blue-600 transition-colors">Terms of Service</Link>
      </div>

      {/* Forgot Password Modal - Rendered via Portal to break out of transformed parents */}
      {showResetModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative animate-scale-in">
            <button
              onClick={() => { setShowResetModal(false); setResetStatus({ type: '', msg: '' }); setResetEmail(''); }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                <svg className="w-7 h-7 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800">Reset Password</h3>
              <p className="text-sm text-gray-500 mt-2 px-4">Enter the email associated with your account and we'll send you a link to reset your password.</p>
            </div>

            {resetStatus.msg && (
              <div className={`text-sm p-3 rounded-lg mb-4 text-center font-medium ${resetStatus.type === 'error' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-green-50 text-green-600 border border-green-100'}`}>
                {resetStatus.msg}
              </div>
            )}

            <form onSubmit={handlePasswordReset}>
              <div className="mb-4">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5 ml-1">Email Address</label>
                <input
                  type="email"
                  required
                  className="w-full bg-slate-50 px-4 py-3 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-gray-800 placeholder-gray-400"
                  placeholder="name@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-lg hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={resetStatus.type === 'loading' || resetStatus.type === 'success'}
              >
                {resetStatus.type === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    Sending...
                  </span>
                ) : 'Send Reset Link'}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
